<template>
  <div
    v-if="(isSupplyOrganization || isCuratorOrganization) && isNGOrPreferredDeal"
    class="form-group row">
    <FormLabel>{{ capitalize(i18n.t('messages.alwaysOn')) }}</FormLabel>
    <div
      id="edit_package_is_evergreen"
      class="col-sm-8">
      <ToggleButton
        :color="colors.success"
        :value="isEvergreenDeal"
        :labels="{ checked: capitalize(i18n.t('actions.yes')), unchecked: capitalize(i18n.t('actions.no')) }"
        @change="handleEvergreenTypeChange" />
    </div>
  </div>
  <div
    v-if="isEvergreenDeal"
    class="form-group row">
    <FormLabel
      for-i-d="edit_package_min_start_date"
      :is-required="true">
      {{ capitalize(i18n.t('names.startDate', 1)) }}
    </FormLabel>
    <div class="col-sm-4">
      <date-picker
        id="edit_package_min_start_date"
        :date="startDate"
        name="minStartDate"
        :min-date="minDate"
        :allow-empty="false"
        class="daterangepicker-input"
        :class="{ 'is-danger': errors.has('minStartDate') }"
        @date-changed="
          (date) => {
            updateMinStartDate(date);
          }
        " />
      <span
        v-show="errors.has('minStartDate')"
        class="help is-danger"
        >{{ errors.first('minStartDate') }}</span
      >
    </div>
  </div>
  <div
    v-else
    class="form-group row">
    <div
      class="col-4 offset-3 mb-3"
      style="padding-left: 2.8rem">
      {{ capitalize(i18n.t('names.start', 1)) }}
    </div>
    <div
      class="col-4 mb-3"
      style="padding-left: 2.8rem">
      {{ !isEvergreenDeal ? capitalize(i18n.t('names.end', 1)) : '' }}
    </div>
    <div class="col-1" />
    <form-label
      for-i-d="edit_package_min_start_date"
      :is-required="true">
      <span>{{ capitalize(i18n.t('messages.dealRunPeriod')) }}</span>
    </form-label>
    <div class="col-sm-4">
      <date-picker
        id="edit_package_min_start_date"
        :date="startDate"
        name="minStartDate"
        :min-date="minDate"
        :allow-empty="false"
        class="daterangepicker-input"
        :class="{ 'is-danger': errors.has('minStartDate') }"
        @date-changed="
          (date) => {
            updateMinStartDate(date);
          }
        " />
      <span
        v-show="errors.has('minStartDate')"
        class="help is-danger"
        >{{ errors.first('minStartDate') }}</span
      >
    </div>

    <div class="col-sm-4">
      <date-picker
        id="edit_package_max_end_date"
        :date="endDate"
        name="maxEndDate"
        :min-date="startDate"
        :allow-empty="false"
        class="daterangepicker-input"
        :class="{ 'is-danger': errors.has('maxEndDate') }"
        @date-changed="
          (date) => {
            updateMaxEndDate(date);
          }
        " />
      <span
        v-show="errors.has('maxEndDate')"
        class="help is-danger"
        >{{ errors.first('maxEndDate') }}</span
      >
    </div>
    <div class="col-sm-1 field-help">
      <div v-tooltip.left="capitalize(i18n.t('messages.fieldDescriptionPackageRunWindow'))">
        <icon-info />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, inject, Ref, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import Colors from '@/Colors';
  import { DatePicker, FormLabel } from '@/common/components/controls';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { IconInfo } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import { EVERGREEN_DATE } from '@/common/models/package/Package';
  import { useInjected } from '@/globals';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { mapOrgAppGetters } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';

  interface IProps {
    isNew: boolean;
    isNGOrPreferredDeal: boolean;
  }
  const { isNew, isNGOrPreferredDeal } = defineProps<IProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');
  const errors: Ref<MessageCollection> = inject('errors', null);

  const { isSupplyOrganization, isCuratorOrganization } = mapOrgAppGetters();
  const i18n = useI18n();
  const colors = ref(Colors);

  const minDate = ref(DateTime.local().startOf('day'));

  const emits = defineEmits(['refreshEstimatedDeliveryStats']);

  const { startDate, endDate, isEvergreenDeal } = useDealComputedProperties(deal, {
    startDate: () => emits('refreshEstimatedDeliveryStats'),
    endDate: () => emits('refreshEstimatedDeliveryStats'),
  });

  function handleEvergreenTypeChange() {
    isEvergreenDeal.value = !isEvergreenDeal.value;
    if (isEvergreenDeal.value) {
      updateMaxEndDate(DateTime.fromISO(EVERGREEN_DATE));
    } else if (isNew) {
      updateMaxEndDate(DateTime.local().startOf('day').plus({ weeks: 1 }).minus({ days: 1 }));
    }
  }

  function updateMinStartDate(date) {
    startDate.value = date;
  }

  function updateMaxEndDate(date) {
    endDate.value = DateTime.fromISO(date);
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables.scss' as *;
</style>
